import React from "react";
import { Box, Tabs, Tab, Typography, Button, Chip, TextField, MenuItem, Dialog, IconButton, TablePagination } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import moment from "moment-timezone";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { tokens } from "../../theme";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData, getAllFilterData, updateData } from "../../Utility/API";
import { getSessonId } from "../../Utility/axiosInstances";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import { FactRows, ConfirmDialog, TablePaginationActions } from "../../components/tableKit";

const mono = '"IBM Plex Mono", monospace';
const pill = (label, kind) => {
  const map = {
    ok: ["rgba(35,214,160,0.13)", tokens.secondary],
    warn: ["rgba(245,166,35,0.13)", tokens.amber],
    bad: ["rgba(242,92,102,0.13)", tokens.danger],
    neutral: [tokens.surface2, tokens.textDim],
  };
  const [bg, c] = map[kind] || map.neutral;
  return <Chip size="small" label={label} sx={{ bgcolor: bg, color: c, fontWeight: 600, height: 20 }} />;
};
const kycKind = (s) => (s === "Approved" ? "ok" : s === "Rejected" ? "bad" : "warn");
const txKind = (s) => {
  const v = String(s || "").toLowerCase();
  if (v.includes("approv")) return "ok";
  if (v.includes("cancel") || v.includes("reject")) return "bad";
  return "warn";
};

const th = { textAlign: "left", color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, padding: "10px 12px", borderBottom: `1px solid ${tokens.border}`, whiteSpace: "nowrap" };
const td = { padding: "11px 12px", borderBottom: `1px solid ${tokens.border}`, fontSize: 12.5, verticalAlign: "middle", whiteSpace: "nowrap" };

const filterSx = { minWidth: 140, "& .MuiInputBase-root": { fontSize: 13 } };

const KYC_STATUS_OPTIONS = ["All", "Pending", "Approved", "Rejected"];
// Same values the player-facing KYC form submits, so an admin edit cannot
// introduce a doc type the rest of the system does not understand.
const KYC_DOC_TYPES = [
  { value: "PASSPORT", label: "Passport" },
  { value: "DRIVER_LICENSE", label: "Driver License" },
];
// The three document slots on a KYC record, in display order.
const KYC_DOC_SLOTS = [
  { key: "selfie", label: "Selfie" },
  { key: "front", label: "Document Front" },
  { key: "back", label: "Document Back" },
];
const WD_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "0", label: "Pending" },
  { value: "1", label: "Approved" },
  { value: "2", label: "Hold" },
  { value: "3", label: "Cancelled" },
];

// Risk tint carried over from the Payments page: flags suspicious
// bet-payment / deposit-source combinations.
const wdRowBg = (row) => {
  if (!row.payWithBet && row.depositSource == "Different") return "rgba(242,92,102,0.10)";
  if ((!row.payWithBet && row.depositSource == "Same") || (row.payWithBet && row.depositSource == "Different"))
    return "rgba(245,166,35,0.08)";
  return "transparent";
};

// Single KYC document image inside the preview dialog.
const DocImage = ({ label, path }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, mb: 0.7 }}>
      {label}
    </Typography>
    {path ? (
      <Box
        component="img"
        src={`${endPoints.apiBaseUrl}${path}`}
        alt={label}
        sx={{ width: "100%", maxHeight: 420, objectFit: "contain", border: `1px solid ${tokens.border}`, borderRadius: "10px", background: tokens.surface2 }}
      />
    ) : (
      <Typography sx={{ fontSize: 12.5, color: tokens.textDim2 }}>No {label.toLowerCase()} available</Typography>
    )}
  </Box>
);

export default function Approvals() {
  const [tab, setTab] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  // ---- KYC tab state ----
  const [kycRows, setKycRows] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [selectedKYC, setSelectedKYC] = React.useState(null);
  const [kycAction, setKycAction] = React.useState(null); // { status: "Approved" | "Rejected", row }
  const [kycEdit, setKycEdit] = React.useState(null); // row being edited
  const [kycEditDocs, setKycEditDocs] = React.useState({ selfie: "", front: "", back: "", doc_type: "" });
  const [uploadingSlot, setUploadingSlot] = React.useState(null); // "selfie" | "front" | "back"
  const [kycPage, setKycPage] = React.useState(0);
  const [kycRowsPerPage, setKycRowsPerPage] = React.useState(5);

  // ---- Withdrawals tab state (ported from /Payments) ----
  const [wdRows, setWdRows] = React.useState([]);
  const [wdTotal, setWdTotal] = React.useState(0);
  const [wdPage, setWdPage] = React.useState(1);
  const [wdRowsPerPage, setWdRowsPerPage] = React.useState(5);
  const [wdUserId, setWdUserId] = React.useState("");
  const [wdName, setWdName] = React.useState("");
  const [wdUsername, setWdUsername] = React.useState("");
  const [wdStatus, setWdStatus] = React.useState("");
  const [wdFromDate, setWdFromDate] = React.useState("");
  const [wdToDate, setWdToDate] = React.useState("");
  const [checkShow, setCheckShow] = React.useState(false);
  const [pauseShow, setPauseShow] = React.useState(false);
  const [cancleShow, setCancleShow] = React.useState(false);
  const [viewShow, setViewShow] = React.useState(false);
  const [editData, setEditData] = React.useState({});

  const wdFilters = {
    userId: wdUserId,
    status: wdStatus,
    name: wdName,
    userName: wdUsername,
    from_date: wdFromDate,
    to_date: wdToDate,
  };

  React.useEffect(() => {
    fetchKYCData("", "All");
    fetchWithdrawals(1, wdFilters);
  }, []);

  React.useEffect(() => {
    fetchWithdrawals(wdPage, wdFilters);
  }, [wdRowsPerPage]);

  // ---- KYC handlers ----
  const fetchKYCData = async (search = "", status = "All") => {
    const params = new URLSearchParams();
    params.append("search", search);
    params.append("status", status);

    let url = `${endPoints.api.GET_KYC_LIST}?${params.toString()}`;
    setIsLoading(true);
    await getAllData(url)
      .then((response) => {
        setIsLoading(false);
        if (response?.status === "success" || response?.status === 200) {
          setKycRows(response.data || []);
        } else {
          ErrorToast(response?.message || "Failed to fetch KYC data");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast("An error occurred while fetching KYC data");
      });
  };

  const approveKyc = async (id) => {
    try {
      let url = `${endPoints.api.UPDATE_KYC_STATUS}/?id=${id}&status=Approved`;
      setIsLoading(true);
      let response = await updateData(url);
      setIsLoading(false);
      if (response?.data?.status == "success") {
        SuccesToast(response?.data?.message);
        fetchKYCData(searchValue, statusFilter);
      } else {
        ErrorToast(response?.data?.message);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setIsLoading(false);
    }
  };

  const rejectKyc = async (row) => {
    try {
      let url = `${endPoints.api.UPDATE_KYC_STATUS}?id=${row?.id}&status=Rejected`;
      setIsLoading(true);
      let response = await updateData(url);
      setIsLoading(false);
      if (response?.data?.status == "success") {
        SuccesToast(response.data.message);
        fetchKYCData(searchValue, statusFilter);
      } else {
        ErrorToast(response?.data?.message);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setIsLoading(false);
    }
  };

  const kycFacts = (d) => [
    ["Request", d?.id ? `#${d.id}` : "—"],
    ["Customer ID", d?.idUser],
    ["Player Name", d?.name],
    ["Email", d?.email],
    ["Doc Type", d?.doc_type],
    ["Submitted", d?.createdOn ? moment.utc(d.createdOn).format("YYYY-MM-DD") : "—"],
  ];

  // Both the row buttons and the preview dialog route through here, so a KYC
  // decision is never a single click.
  const confirmKycAction = async () => {
    if (!kycAction) return;
    const { status, row } = kycAction;
    setKycAction(null);
    setSelectedKYC(null);
    if (status === "Approved") await approveKyc(row?.id);
    else await rejectKyc(row);
  };

  // ---- KYC document editing ----
  // Opens the edit dialog seeded with what is currently on the record, so
  // untouched slots are re-sent unchanged (the API rewrites all three).
  const openKycEdit = (row) => {
    setSelectedKYC(null);
    setKycEdit(row);
    setKycEditDocs({
      selfie: row?.selfie || "",
      front: row?.front || "",
      back: row?.back || "",
      doc_type: row?.doc_type || "",
    });
  };

  const closeKycEdit = () => {
    setKycEdit(null);
    setUploadingSlot(null);
  };

  // Uploads a replacement file and stores the returned path against its slot.
  // Nothing is persisted until the dialog is saved.
  const uploadKycDoc = (slot, file) => {
    if (!file) return;
    const formdata = new FormData();
    formdata.append("file", file);
    setUploadingSlot(slot);
    // This is a raw fetch (multipart), so it misses the axios interceptor that
    // attaches SessonId — set it by hand. Content-Type is deliberately left
    // unset so the browser can add the multipart boundary.
    const sessonId = getSessonId();
    fetch(`${endPoints.apiBaseUrl}${endPoints.api.UPLOAD_KYC_DOC}`, {
      method: "POST",
      body: formdata,
      redirect: "follow",
      headers: {
        Accept: "application/json",
        ...(sessonId ? { SessonId: sessonId } : {}),
      },
    })
      .then((response) => response.json())
      .then((result) => {
        setUploadingSlot(null);
        if (result?.status === "success" && result?.data) {
          setKycEditDocs((prev) => ({ ...prev, [slot]: result.data }));
          SuccesToast("Document uploaded");
        } else {
          // On failure this endpoint puts a Throwable in `message`, so only
          // surface it when it actually came back as text.
          ErrorToast(typeof result?.message === "string" ? result.message : "Document upload failed (JPG or PNG only)");
        }
      })
      .catch((error) => {
        setUploadingSlot(null);
        console.error(error);
        ErrorToast("An error occurred while uploading the document");
      });
  };

  // The save endpoint rewrites selfie/front/back/doc_type/status in one go, so the
  // current status is sent back verbatim — editing documents must not silently
  // re-open or close a decision.
  const saveKycEdit = async () => {
    if (!kycEdit) return;
    const payload = {
      id: kycEdit.id,
      idUser: kycEdit.idUser,
      selfie: kycEditDocs.selfie,
      front: kycEditDocs.front,
      back: kycEditDocs.back,
      doc_type: kycEditDocs.doc_type,
      status: kycEdit.status,
    };
    try {
      setIsLoading(true);
      const response = await addData(endPoints.api.SAVE_KYC, payload);
      setIsLoading(false);
      if (response?.data?.status === "success") {
        SuccesToast(response?.data?.message || "KYC documents updated");
        closeKycEdit();
        fetchKYCData(searchValue, statusFilter);
      } else {
        ErrorToast(response?.data?.message || "Failed to update KYC documents");
      }
    } catch (err) {
      console.error(err);
      ErrorToast("An error occurred while updating KYC documents");
    } finally {
      setIsLoading(false);
    }
  };

  const searchKyc = () => {
    setKycPage(0);
    fetchKYCData(searchValue, statusFilter);
  };

  // ---- Withdrawal handlers (same endpoints & payloads as /Payments) ----
  const fetchWithdrawals = async (page, data) => {
    let url = `${endPoints.api.WITHDRAW_TRANSACTIONS}/${page}/${wdRowsPerPage}`;
    setIsLoading(true);
    await getAllFilterData(url, data)
      .then((response) => {
        setIsLoading(false);
        if (response?.status === "success") {
          setWdRows(response.data || []);
          setWdTotal(response.data?.[0]?.totalcount || 0);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const updateWithdrawal = async (alldata, status, closeDialog) => {
    try {
      let url = `${endPoints.api.TRANSACTION_UPDATE}`;
      let data = {
        id_transaction: alldata.id_transaction,
        id_user: alldata.id_user,
        status: status,
      };
      setIsLoading(true);
      let response = await getAllFilterData(url, data);
      setIsLoading(false);
      if (response?.status == "success") {
        SuccesToast(response.message);
        fetchWithdrawals(wdPage, wdFilters);
        closeDialog();
      } else {
        ErrorToast(response?.message);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setIsLoading(false);
    }
  };

  const Approval = (row) => updateWithdrawal(row, 1, () => setCheckShow(false));
  const Hold = (row) => updateWithdrawal(row, 2, () => setPauseShow(false));
  const Cancel = (row) => updateWithdrawal(row, 3, () => setCancleShow(false));

  const searchWithdrawals = () => {
    setWdPage(1);
    fetchWithdrawals(1, wdFilters);
  };

  const wdKeyDown = (e) => {
    if (e.key === "Enter") searchWithdrawals();
  };

  const txFacts = (d) => [
    ["Customer ID", d?.id_user],
    ["Transaction ID", d?.id_transaction],
    ["Customer Name", d?.name],
    ["Transaction Date", d?.insertion_time ? moment.utc(d.insertion_time).format("YYYY-MM-DD") : "—"],
    ["User Name", d?.login],
    ["Amount", d?.tamount != null ? `$${d.tamount}` : "—"],
  ];

  const pendingCount = kycRows.filter((r) => r.status === "Pending").length;

  return (
    <Box>
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader title="Approvals" crumb="Operate / Approvals" subtitle="KYC & withdrawals · human-in-the-loop" />

      {pendingCount > 0 && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 1.5, mb: 1.75, borderRadius: "10px", borderLeft: `3px solid ${tokens.amber}`, border: `1px solid ${tokens.border}`, background: tokens.cardBg }}>
          <WarningAmberRoundedIcon sx={{ color: tokens.amber, fontSize: 18 }} />
          <Typography variant="caption" color="text.secondary"><b>{pendingCount}</b> KYC submission{pendingCount === 1 ? "" : "s"} pending review — clearing the backlog unblocks withdrawals.</Typography>
        </Box>
      )}

      <SectionCard sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${tokens.border}`, minHeight: 44, "& .MuiTab-root": { textTransform: "none", minHeight: 44, fontSize: 13, fontWeight: 600, color: tokens.textDim }, "& .Mui-selected": { color: tokens.primary }, "& .MuiTabs-indicator": { backgroundColor: tokens.primary } }}>
          <Tab label={<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>KYC {pendingCount > 0 && pill(pendingCount, "warn")}</Box>} />
          <Tab label={<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>Withdrawals {wdTotal > 0 && pill(wdTotal, "warn")}</Box>} />
        </Tabs>

        <Box sx={{ p: 2, overflowX: "auto" }}>
          {tab === 0 && (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search by User ID, name, address or city"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") searchKyc();
                  }}
                  sx={{ minWidth: 280, "& .MuiInputBase-root": { fontSize: 13 } }}
                />
                <TextField
                  select
                  size="small"
                  label="KYC Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ minWidth: 150, "& .MuiInputBase-root": { fontSize: 13 } }}
                >
                  {KYC_STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" size="small" onClick={searchKyc} sx={{ textTransform: "none" }}>
                  Search
                </Button>
              </Box>

              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Request</th>
                    <th style={th}>Player</th>
                    <th style={th}>Address</th>
                    <th style={th}>Birth Date</th>
                    <th style={th}>Doc Type</th>
                    <th style={th}>Submitted</th>
                    <th style={th}>Docs</th>
                    <th style={th}>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {kycRows.length === 0 && !isLoading && (
                    <tr>
                      <td colSpan={8} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                        No KYC submissions found
                      </td>
                    </tr>
                  )}
                  {kycRows
                    .slice(kycPage * kycRowsPerPage, kycPage * kycRowsPerPage + kycRowsPerPage)
                    .map((k) => (
                    <tr key={k.id}>
                      <td style={{ ...td, fontFamily: mono }}>#{k.id}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{k.name}</div>
                        <div style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>#{k.idUser} · {k.email}</div>
                      </td>
                      <td style={{ ...td, color: tokens.textDim }}>
                        {[k.address, k.city].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{k.birthDate || "—"}</td>
                      <td style={td}>{k.doc_type ? pill(k.doc_type, "neutral") : "—"}</td>
                      <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>
                        {k.createdOn ? moment.utc(k.createdOn).format("YYYY-MM-DD") : ""}
                      </td>
                      <td style={td}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={() => setSelectedKYC(k)}
                            sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}
                          >
                            Preview
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={() => openKycEdit(k)}
                            sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}
                          >
                            Edit
                          </Button>
                        </Box>
                      </td>
                      <td style={td}>
                        {k.status === "Pending" ? (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button size="small" variant="outlined" onClick={() => setKycAction({ status: "Approved", row: k })} sx={{ textTransform: "none", color: tokens.secondary, borderColor: "rgba(35,214,160,0.4)" }}>Approve</Button>
                            <Button size="small" variant="outlined" onClick={() => setKycAction({ status: "Rejected", row: k })} sx={{ textTransform: "none", color: tokens.danger, borderColor: "rgba(242,92,102,0.4)" }}>Reject</Button>
                          </Box>
                        ) : (
                          pill(k.status, kycKind(k.status))
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>

              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={kycRows.length}
                rowsPerPage={kycRowsPerPage}
                page={kycPage}
                SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                onPageChange={(e, newPage) => setKycPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setKycRowsPerPage(parseInt(e.target.value, 10));
                  setKycPage(0);
                }}
                ActionsComponent={TablePaginationActions}
                sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
              />
            </>
          )}

          {tab === 1 && (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
                <TextField size="small" label="Customer ID" value={wdUserId} onChange={(e) => setWdUserId(e.target.value)} onKeyDown={wdKeyDown} sx={filterSx} />
                <TextField size="small" label="Name" value={wdName} onChange={(e) => setWdName(e.target.value)} onKeyDown={wdKeyDown} sx={filterSx} />
                <TextField size="small" label="Username" value={wdUsername} onChange={(e) => setWdUsername(e.target.value)} onKeyDown={wdKeyDown} sx={filterSx} />
                <TextField select size="small" label="Withdrawal Status" value={wdStatus} onChange={(e) => setWdStatus(e.target.value)} sx={{ ...filterSx, minWidth: 160 }}>
                  {WD_STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={wdFromDate}
                  onChange={(e) => setWdFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={filterSx}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={wdToDate}
                  onChange={(e) => setWdToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={filterSx}
                />
                <Button variant="contained" size="small" onClick={searchWithdrawals} sx={{ textTransform: "none" }}>
                  Search
                </Button>
              </Box>

              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Request</th>
                    <th style={th}>Player</th>
                    <th style={th}>Type</th>
                    <th style={th}>Trans Date</th>
                    <th style={th}>Amount</th>
                    <th style={th}>Status</th>
                    <th style={th}>Account</th>
                    <th style={th}>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {wdRows.length === 0 && !isLoading && (
                    <tr>
                      <td colSpan={8} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                        No withdrawal requests found
                      </td>
                    </tr>
                  )}
                  {wdRows.map((w) => (
                    <tr key={w.id_transaction} style={{ background: wdRowBg(w) }}>
                      <td style={{ ...td, fontFamily: mono }}>#{w.id_transaction}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{w.name} {w.surname}</div>
                        <div style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>#{w.id_user} · {w.login}</div>
                      </td>
                      <td style={td}>{pill(w.type == 24 ? "Corepay" : "Skrill", "neutral")}</td>
                      <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>
                        {moment.utc(w.insertion_time).format("YYYY-MM-DD")}
                      </td>
                      <td style={{ ...td, fontFamily: mono, fontWeight: 700 }}>${w.tamount}</td>
                      <td style={td}>{w.transaction_status ? pill(w.transaction_status, txKind(w.transaction_status)) : ""}</td>
                      <td style={td}>{w.accountSatus ? pill(w.accountSatus, txKind(w.accountSatus)) : ""}</td>
                      <td style={td}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
                            sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}
                            onClick={() => {
                              setViewShow(true);
                              setEditData(w);
                            }}
                          >
                            View
                          </Button>
                          {w.status == 1 || w.status == 3 ? null : (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: "none", color: tokens.secondary, borderColor: "rgba(35,214,160,0.4)" }}
                                onClick={() => {
                                  setCheckShow(true);
                                  setEditData(w);
                                }}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={w.status == 2}
                                sx={{ textTransform: "none", color: tokens.amber, borderColor: "rgba(245,166,35,0.4)" }}
                                onClick={() => {
                                  setPauseShow(true);
                                  setEditData(w);
                                }}
                              >
                                Hold
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: "none", color: tokens.danger, borderColor: "rgba(242,92,102,0.4)" }}
                                onClick={() => {
                                  setCancleShow(true);
                                  setEditData(w);
                                }}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>

              <TablePagination
                component="div"
                className="hide-rows-per-page-options"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={wdRowsPerPage === -1 ? wdRows.length : wdTotal || 0}
                rowsPerPage={wdRowsPerPage}
                page={wdPage - 1}
                SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                onPageChange={(e, newPage) => {
                  setWdPage(newPage + 1);
                  fetchWithdrawals(newPage + 1, wdFilters);
                }}
                onRowsPerPageChange={(e) => {
                  setWdRowsPerPage(parseInt(e.target.value, 10));
                }}
                ActionsComponent={TablePaginationActions}
                sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
              />
            </>
          )}
        </Box>
      </SectionCard>

      {/* KYC document preview */}
      <Dialog
        open={Boolean(selectedKYC)}
        onClose={() => setSelectedKYC(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { background: tokens.cardBg, border: `1px solid ${tokens.border}`, borderRadius: "12px" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: `1px solid ${tokens.border}`, position: "sticky", top: 0, zIndex: 1, background: tokens.surface }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 16 }}>KYC Documents</Typography>
            {selectedKYC && (
              <Typography sx={{ fontFamily: mono, fontSize: 11, color: tokens.textDim2 }}>
                #{selectedKYC.idUser} · {selectedKYC.name}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => setSelectedKYC(null)} aria-label="close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5 }}>
          {selectedKYC && (
            <>
              <DocImage label="Selfie" path={selectedKYC.selfie} />
              <DocImage label="Document Front" path={selectedKYC.front} />
              <DocImage label="Document Back" path={selectedKYC.back} />
              <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between", alignItems: "center" }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => openKycEdit(selectedKYC)}
                  sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}
                >
                  Edit Documents
                </Button>
                {selectedKYC.status === "Pending" && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setKycAction({ status: "Approved", row: selectedKYC })}
                      sx={{ textTransform: "none", color: tokens.secondary, borderColor: "rgba(35,214,160,0.4)" }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setKycAction({ status: "Rejected", row: selectedKYC })}
                      sx={{ textTransform: "none", color: tokens.danger, borderColor: "rgba(242,92,102,0.4)" }}
                    >
                      Reject
                    </Button>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {/* KYC document editor — replace any of the three files and/or the doc type */}
      <Dialog
        open={Boolean(kycEdit)}
        onClose={closeKycEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { background: tokens.cardBg, border: `1px solid ${tokens.border}`, borderRadius: "12px" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: `1px solid ${tokens.border}`, position: "sticky", top: 0, zIndex: 1, background: tokens.surface }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 16 }}>Edit KYC Documents</Typography>
            {kycEdit && (
              <Typography sx={{ fontFamily: mono, fontSize: 11, color: tokens.textDim2 }}>
                #{kycEdit.idUser} · {kycEdit.name}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={closeKycEdit} aria-label="close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5 }}>
          {kycEdit && (
            <>
              <TextField
                select
                size="small"
                fullWidth
                label="Document Type"
                value={kycEditDocs.doc_type}
                onChange={(e) => setKycEditDocs((prev) => ({ ...prev, doc_type: e.target.value }))}
                sx={{ mb: 2.5, "& .MuiInputBase-root": { fontSize: 13 } }}
              >
                <MenuItem value="">Not set</MenuItem>
                {KYC_DOC_TYPES.map((d) => (
                  <MenuItem key={d.value} value={d.value} sx={{ fontSize: 13 }}>{d.label}</MenuItem>
                ))}
              </TextField>

              {KYC_DOC_SLOTS.map(({ key, label }) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <DocImage label={label} path={kycEditDocs[key]} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: -1 }}>
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={Boolean(uploadingSlot)}
                      sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}
                    >
                      {uploadingSlot === key ? "Uploading…" : kycEditDocs[key] ? `Replace ${label}` : `Upload ${label}`}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={(e) => {
                          uploadKycDoc(key, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </Button>
                    {kycEditDocs[key] !== (kycEdit[key] || "") && (
                      <Typography sx={{ fontSize: 11, color: tokens.amber }}>Replaced — not saved yet</Typography>
                    )}
                  </Box>
                </Box>
              ))}

              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Saving keeps the submission at <b>{kycEdit.status}</b>; use Approve or Reject to change the decision.
              </Typography>

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button size="small" variant="outlined" color="inherit" onClick={closeKycEdit} sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={Boolean(uploadingSlot)}
                  onClick={saveKycEdit}
                  sx={{ textTransform: "none", fontWeight: 600, color: tokens.secondary, borderColor: "rgba(35,214,160,0.4)" }}
                >
                  Save Changes
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {/* KYC decision confirmation */}
      <ConfirmDialog
        open={Boolean(kycAction)}
        onClose={() => setKycAction(null)}
        title={kycAction?.status === "Rejected" ? "Reject KYC Submission" : "Approve KYC Submission"}
        facts={kycFacts(kycAction?.row)}
        question={
          kycAction?.status === "Rejected"
            ? "Would you like to reject this KYC submission?"
            : "Would you like to approve this KYC submission?"
        }
        note={
          kycAction?.status === "Rejected"
            ? "The player will stay unverified and will need to resubmit their documents."
            : "The player will be marked verified, which unblocks their withdrawals."
        }
        actionLabel={kycAction?.status === "Rejected" ? "Reject KYC" : "Approve KYC"}
        actionColor={kycAction?.status === "Rejected" ? tokens.danger : tokens.secondary}
        onAction={confirmKycAction}
      />

      {/* Withdrawal decision dialogs (ported from /Payments) */}
      <ConfirmDialog
        open={checkShow}
        onClose={() => setCheckShow(false)}
        title="Withdrawal Approval"
        facts={txFacts(editData)}
        question="Would you like to approve this transaction?"
        note="After approval, funds will be sent to the payment provider."
        actionLabel="Approve Withdrawal"
        actionColor={tokens.secondary}
        onAction={() => Approval(editData)}
      />
      <ConfirmDialog
        open={pauseShow}
        onClose={() => setPauseShow(false)}
        title="Place Withdrawal On Hold"
        facts={txFacts(editData)}
        question="Would you like to put this transaction on hold?"
        note="This transaction will be held while you investigate. You can return later to approve or cancel."
        actionLabel="Put on Hold"
        actionColor={tokens.amber}
        onAction={() => Hold(editData)}
      />
      <ConfirmDialog
        open={cancleShow}
        onClose={() => setCancleShow(false)}
        title="Withdrawal Cancellation"
        facts={txFacts(editData)}
        question="Would you like to cancel this transaction?"
        note="After cancellation funds will be returned to the player’s account."
        actionLabel="Cancel Withdrawal"
        actionColor={tokens.danger}
        onAction={() => Cancel(editData)}
      />

      {/* Withdrawal details (facts + per-source transaction history) */}
      <Dialog
        open={viewShow}
        onClose={() => setViewShow(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { background: tokens.cardBg, border: `1px solid ${tokens.border}`, borderRadius: "12px" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: `1px solid ${tokens.border}` }}>
          <Typography variant="h6" sx={{ fontSize: 16 }}>Withdrawal Details</Typography>
          <IconButton size="small" onClick={() => setViewShow(false)} aria-label="close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <FactRows
            facts={[
              ...txFacts(editData),
              ["Withdrawal Status", editData?.transaction_status],
              ["Wallet Type", editData?.wallet_type],
              ...(editData?.corepay
                ? [
                    ["Account Number", editData.corepay.accountnumber],
                    ["Routing Number", editData.corepay.routingnumber],
                  ]
                : []),
              ["Status Updated", editData?.updated_on ? moment.utc(editData.updated_on).format("YYYY-MM-DD") : "—"],
              ["Updated By", editData?.adminname],
              ["Transaction Type", editData?.type == 24 ? "Corepay" : "Skrill"],
              ["Pay Bets", editData?.payBets],
              ["Total Deposit", editData?.totalSkrilDeposit],
              ["Total Withdrawal", editData?.totalSkrilWithdrawal],
              ["Account Status", editData?.accountSatus],
              ["Rational", editData?.reason],
            ]}
          />
        </Box>
      </Dialog>
    </Box>
  );
}
